import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BlockStack, Text } from '@shopify/polaris';
import {
  normalizeTextStyle,
  replaceKidNameToken,
} from '../../utils/textStyleLayout';

const BASE_WIDTH = 1152;
const BASE_HEIGHT = 1120;
const TEXT_CONFIGS = {
  online: { shiftPx: 70.8661, fontScale: 0.9 },
  offline: { shiftPx: 70.8661, fontScale: 0.9 },
};

const FONT_STYLE_META = {
  regular: { fontWeight: 400, fontStyle: 'normal', variantKey: 'regular' },
  italic: { fontWeight: 400, fontStyle: 'italic', variantKey: 'italic' },
  bold: { fontWeight: 700, fontStyle: 'normal', variantKey: 'bold' },
  bold_italic: { fontWeight: 700, fontStyle: 'italic', variantKey: 'bold_italic' },
};

const inferFontFormat = (url) => {
  const normalized = String(url || '').toLowerCase();
  if (normalized.includes('.woff2')) return 'woff2';
  if (normalized.includes('.woff')) return 'woff';
  if (normalized.includes('.otf')) return 'opentype';
  return 'truetype';
};

const TextScenePreview = ({
  imageUrl,
  text,
  kidName,
  language,
  order,
  source = 'online',
  style: styleValue,
  fonts = [],
  generatedPreviewUrl = '',
}) => {
  const hostRef = useRef(null);
  const frameRef = useRef(null);
  const overlayRef = useRef(null);
  const [hostWidth, setHostWidth] = useState(420);
  const [fittedFontSize, setFittedFontSize] = useState(null);
  const [overflowing, setOverflowing] = useState(false);
  const style = normalizeTextStyle(styleValue || {});
  const isArabicText = String(language || '').toLowerCase().includes('arabic') ||
    /[\u0600-\u06ff]/.test(String(text || ''));

  useEffect(() => {
    if (!hostRef.current) return undefined;
    const host = hostRef.current;
    const updateWidth = () => {
      const width = host.getBoundingClientRect().width;
      if (Number.isFinite(width) && width > 0) setHostWidth(width);
    };
    updateWidth();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(updateWidth);
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  const fontRecord = useMemo(() => {
    const key = String(style.font_key || '').trim().toLowerCase();
    if (key) {
      return fonts.find((font) => String(font?.key || '').trim().toLowerCase() === key) || null;
    }
    return isArabicText
      ? fonts.find((font) => font?.is_default && ['arabic', 'mixed'].includes(font?.script)) || null
      : null;
  }, [fonts, isArabicText, style.font_key]);

  const fontMeta = FONT_STYLE_META[style.font_style] || FONT_STYLE_META.regular;
  const fontUrl = fontRecord
    ? (fontRecord?.variants?.[fontMeta.variantKey] || fontRecord?.variants?.regular || '')
    : '';
  const fallbackFamily = isArabicText ? 'TheYearofTheCamel' : 'PingLCG';
  const familyAlias = fontRecord
    ? `StoryTextPreview_${String(fontRecord.key || 'font').replace(/[^a-zA-Z0-9_-]/g, '_')}_${style.font_style}`
    : fallbackFamily;
  const fontFaceCss = fontUrl
    ? `@font-face { font-family: '${familyAlias}'; src: url('${fontUrl}') format('${inferFontFormat(fontUrl)}'); font-weight: ${fontMeta.fontWeight}; font-style: ${fontMeta.fontStyle}; font-display: swap; }`
    : '';
  const previewText = replaceKidNameToken(text, kidName);
  const direction = isArabicText ? 'rtl' : 'ltr';
  const textConfig = TEXT_CONFIGS[source] || TEXT_CONFIGS.online;
  const baseFontSize = style.font_size || (Math.floor(BASE_WIDTH / 25) * textConfig.fontScale);
  const parsedOrder = Number(order);
  const xOffsetPx = Number.isFinite(parsedOrder)
    ? (parsedOrder % 2 === 0 ? -textConfig.shiftPx : textConfig.shiftPx)
    : 0;

  useEffect(() => {
    if (style.layout_mode === 'frame' && style.auto_fit) {
      setOverflowing(false);
      return;
    }
    setFittedFontSize(baseFontSize);
    setOverflowing(false);
  }, [
    baseFontSize,
    imageUrl,
    previewText,
    style.layout_mode,
    style.frame_margin_top,
    style.frame_margin_right,
    style.frame_margin_bottom,
    style.frame_margin_left,
    style.frame_width_percent,
    style.auto_fit,
    style.min_font_size,
    familyAlias,
  ]);

  useEffect(() => {
    if (
      style.layout_mode !== 'frame' ||
      !style.auto_fit ||
      !frameRef.current ||
      !overlayRef.current
    ) {
      return undefined;
    }

    let cancelled = false;
    const fitText = async () => {
      try {
        if (document.fonts?.ready) await document.fonts.ready;
      } catch (_) {
        // Browser fallback fonts are still measurable.
      }
      if (cancelled || !frameRef.current || !overlayRef.current) return;

      const frame = frameRef.current;
      const overlay = overlayRef.current;
      const previousFontSize = overlay.style.fontSize;
      const minimum = Math.min(baseFontSize, Math.max(1, style.min_font_size || 1));
      let low = minimum;
      let high = Math.max(minimum, baseFontSize);
      let best = minimum;
      const fits = (fontSize) => {
        overlay.style.fontSize = `${fontSize}px`;
        return overlay.scrollHeight <= frame.clientHeight + 1 &&
          overlay.scrollWidth <= overlay.clientWidth + 1;
      };

      if (fits(high)) {
        best = high;
      } else if (!fits(low)) {
        best = low;
      } else {
        high -= 1;
        while (low <= high) {
          const midpoint = Math.floor((low + high) / 2);
          if (fits(midpoint)) {
            best = midpoint;
            low = midpoint + 1;
          } else {
            high = midpoint - 1;
          }
        }
      }

      const doesOverflow = !fits(best);
      overlay.style.fontSize = previousFontSize;
      if (!cancelled) {
        setFittedFontSize(best);
        setOverflowing(doesOverflow);
      }
    };

    const frameId = requestAnimationFrame(fitText);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
    };
  }, [
    baseFontSize,
    previewText,
    style.auto_fit,
    style.frame_width_percent,
    style.layout_mode,
    style.min_font_size,
    style.frame_margin_top,
    style.frame_margin_right,
    style.frame_margin_bottom,
    style.frame_margin_left,
    familyAlias,
    fontMeta.fontStyle,
    fontMeta.fontWeight,
  ]);

  const sharedTextStyle = {
    fontFamily: `'${familyAlias}', '${fallbackFamily}', Arial, sans-serif`,
    fontWeight: fontMeta.fontWeight,
    fontStyle: fontMeta.fontStyle,
    fontSize: `${fittedFontSize || baseFontSize}px`,
    color: '#ffffff',
    textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
    textAlign: style.text_align,
    direction,
    lineHeight: 1.4,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    zIndex: 2,
  };

  const absoluteOverlayStyle = (() => {
    const width = style.x !== null || style.y !== null
      ? BASE_WIDTH - 100
      : Math.floor(BASE_WIDTH * 0.7);
    if (style.x !== null && style.y !== null) {
      return {
        ...sharedTextStyle,
        position: 'absolute',
        left: `${style.x}px`,
        top: `${style.y}px`,
        width,
        transform: 'translateX(-50%)',
      };
    }
    if (style.x === null && style.y !== null) {
      return {
        ...sharedTextStyle,
        position: 'absolute',
        left: '50%',
        top: `${style.y}px`,
        width,
        transform: 'translateX(-50%)',
      };
    }
    if (style.x !== null && style.y === null) {
      return {
        ...sharedTextStyle,
        position: 'absolute',
        left: `${style.x}px`,
        top: '50%',
        width,
        transform: 'translate(-50%, -50%)',
      };
    }
    return {
      ...sharedTextStyle,
      position: 'absolute',
      left: xOffsetPx ? `calc(50% + ${xOffsetPx}px)` : '50%',
      top: '50%',
      width,
      padding: '0 24px',
      transform: 'translate(-50%, -50%)',
    };
  })();

  const verticalAlign = {
    top: 'start',
    center: 'center',
    bottom: 'end',
  }[style.vertical_align] || 'center';
  const horizontalAlign = {
    start: direction === 'rtl' ? 'end' : 'start',
    center: 'center',
    end: direction === 'rtl' ? 'start' : 'end',
  }[style.horizontal_align] || 'center';
  const scale = Math.max(0.2, hostWidth / BASE_WIDTH);

  return (
    <BlockStack gap="200">
      {fontFaceCss ? <style>{fontFaceCss}</style> : null}
      <div ref={hostRef} style={{ width: '100%', maxWidth: 560 }}>
        <div
          style={{
            position: 'relative',
            width: '100%',
            paddingTop: `${(BASE_HEIGHT / BASE_WIDTH) * 100}%`,
            borderRadius: 8,
            overflow: 'hidden',
            background: '#f1f2f3',
            border: '1px solid #d8dbde',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              width: BASE_WIDTH,
              height: BASE_HEIGHT,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
            }}
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="Text scene"
                style={{ width: BASE_WIDTH, height: BASE_HEIGHT, objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <div style={{ width: BASE_WIDTH, height: BASE_HEIGHT, background: '#e4e7eb' }} />
            )}

            {style.layout_mode === 'frame' ? (
              <div
                ref={frameRef}
                style={{
                  position: 'absolute',
                  top: style.frame_margin_top,
                  right: style.frame_margin_right,
                  bottom: style.frame_margin_bottom,
                  left: style.frame_margin_left,
                  display: 'grid',
                  alignItems: verticalAlign,
                  justifyItems: horizontalAlign,
                  overflow: 'hidden',
                  transform: xOffsetPx ? `translateX(${xOffsetPx}px)` : undefined,
                }}
              >
                <div
                  ref={overlayRef}
                  style={{
                    ...sharedTextStyle,
                    width: `${style.frame_width_percent}%`,
                    maxHeight: '100%',
                  }}
                >
                  {previewText || 'Type scene text to preview'}
                </div>
              </div>
            ) : (
              <div ref={overlayRef} style={absoluteOverlayStyle}>
                {previewText || 'Type scene text to preview'}
              </div>
            )}
          </div>
        </div>
      </div>

      {overflowing ? (
        <Text as="p" variant="bodySm" tone="critical">
          Text still overflows at the minimum font size.
        </Text>
      ) : null}

      {generatedPreviewUrl ? (
        <div style={{ border: '1px solid #d8dbde', borderRadius: 8, padding: 8 }}>
          <Text as="p" variant="bodySm" tone="subdued">Server preview</Text>
          <img
            src={generatedPreviewUrl}
            alt="Server-rendered text scene"
            style={{ width: '100%', borderRadius: 6, display: 'block', marginTop: 6 }}
          />
        </div>
      ) : null}
    </BlockStack>
  );
};

export default TextScenePreview;
